import React from 'react';
import ReactDOM from 'react-dom';
import { Lightbulb, Save, X } from 'lucide-react';

interface ManualFixtureAddModalProps {
  show: boolean;
  fixtureName: string;
  fixtureCode: string;
  onFixtureNameChange: (value: string) => void;
  onFixtureCodeChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const ManualFixtureAddModal: React.FC<ManualFixtureAddModalProps> = ({
  show,
  fixtureName,
  fixtureCode,
  onFixtureNameChange,
  onFixtureCodeChange,
  onSave,
  onCancel,
}) => {
  if (!show) return null;

  const canSave = fixtureName.trim().length > 0 && fixtureCode.trim().length > 0;
  const previewCodes = fixtureCode
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
  const codeSquareSize = 112;
  const getCodeFontSize = (code: string) => {
    if (code.length <= 3) return 34;
    if (code.length <= 5) return 28;
    if (code.length <= 8) return 22;
    if (code.length <= 11) return 18;
    return 15;
  };

  return ReactDOM.createPortal(
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100003,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '560px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '14px',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
          padding: '22px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '14px',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3
              style={{
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              <Lightbulb
                size={18}
                style={{
                  color: '#facc15',
                  filter: 'drop-shadow(0 0 8px rgba(250, 204, 21, 0.8))',
                }}
              />
              Add New Fixture Type
            </h3>
            <p style={{ margin: '6px 0 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
              Add a fixture name and the type code shown on plans.
            </p>
          </div>
          <button
            onClick={onCancel}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 0,
            }}
            aria-label="Close fixture add modal"
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '12px',
              maxWidth: '100%',
            }}
          >
            {previewCodes.length > 0 ? (
              previewCodes.map((code, index) => (
                <div
                  key={`${code}-${index}`}
                  style={{
                    width: `${codeSquareSize}px`,
                    height: `${codeSquareSize}px`,
                    background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #f59e0b',
                    padding: '8px',
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      color: 'var(--yellow-text-dark)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: `${getCodeFontSize(code)}px`,
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      lineHeight: 1.05,
                      textAlign: 'center',
                      wordBreak: 'break-all',
                    }}
                  >
                    {code}
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{
                  width: `${codeSquareSize}px`,
                  height: `${codeSquareSize}px`,
                  background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #f59e0b',
                  padding: '8px',
                  flexShrink: 0,
                }}
              >
                <Lightbulb size={40} style={{ color: 'var(--yellow-text-dark)', opacity: 0.85 }} />
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600 }}>
              Fixture Name
            </span>
            <input
              type="text"
              value={fixtureName}
              onChange={(e) => onFixtureNameChange(e.target.value)}
              placeholder="2x4 LED Troffer"
              autoFocus
              style={{
                width: '100%',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600 }}>
              Fixture Code
            </span>
            <input
              type="text"
              value={fixtureCode}
              onChange={(e) => onFixtureCodeChange(e.target.value)}
              placeholder="A, B1, L0-1"
              style={{
                width: '100%',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '18px',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border-strong)',
              background: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!canSave}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              background: canSave ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'var(--bg-divider)',
              color: canSave ? '#ffffff' : 'var(--text-muted)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Save size={14} />
            Save Fixture
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
